import { computed, reactive, ref } from "vue";
import { memoize } from "lodash-es";

/**
 * @typedef {Object} AccordionSubgroup
 * @property {string} [heading] - Optional sub-heading rendered above this subgroup's items.
 * @property {any[]} items - Searchable items belonging to this subgroup.
 */

/**
 * @typedef {Object} AccordionGroup
 * @property {string} title - Group title; also the accordion's expand/collapse key.
 * @property {AccordionSubgroup[]} subgroups - Subgroups of searchable items within this group.
 */

/**
 * @typedef {Object} HighlightSegment
 * @property {string} text - A slice of the original text.
 * @property {boolean} matched - Whether this slice matches the current query.
 */

/**
 * Full-text filter over a tree of accordion groups > subgroups > items (e.g. feature-flag
 * checkboxes grouped by section). Framework-agnostic to the item shape — only `getLabel` needs
 * to know how to read searchable text off one item.
 *
 * Groups with no matching item are dropped entirely while a query is active, so every group
 * that does render already has a match — bind `isGroupExpanded`/`setGroupExpanded` to a
 * controllable accordion's `opened` model to auto-expand exactly those, and `highlight` to mark
 * the matched substring in a label. Once the query is cleared, expansion falls back to whatever
 * the user last set by clicking the accordion directly (independent of search).
 *
 * @param {import("vue").Ref<AccordionGroup[]> | import("vue").ComputedRef<AccordionGroup[]>} groups - Reactive list of groups.
 * @param {(item: any) => string} getLabel - Extracts the searchable text from one item.
 * @returns {{
 *   query: import("vue").Ref<string>,
 *   filteredGroups: import("vue").ComputedRef<AccordionGroup[]>,
 *   isGroupExpanded: (title: string) => boolean,
 *   setGroupExpanded: (title: string, value: boolean) => void,
 *   highlight: (text: string) => HighlightSegment[],
 * }}
 * @example
 * const { query, filteredGroups, isGroupExpanded, setGroupExpanded, highlight } = useAccordionSearch(
 *   searchableFlagGroups,
 *   descriptor => descriptor.label,
 * );
 */
export const useAccordionSearch = (groups, getLabel) => {
  const query = ref("");
  const manuallyExpanded = reactive({});

  const normalizedQuery = computed(() => query.value.trim().toLowerCase());

  const filteredGroups = computed(() => {
    if (!normalizedQuery.value) return groups.value;

    return groups.value
      .map(group => ({
        ...group,
        subgroups: group.subgroups
          .map(subgroup => ({
            ...subgroup,
            items: subgroup.items.filter(item =>
              getLabel(item).toLowerCase().includes(normalizedQuery.value),
            ),
          }))
          .filter(subgroup => subgroup.items.length > 0),
      }))
      .filter(group => group.subgroups.length > 0);
  });

  // While a query is active, every group still rendering already matched (filteredGroups already
  // dropped the rest) — force it open even if the user had manually collapsed it earlier, so a new
  // search always surfaces its matches instead of hiding them behind a stale collapse. Manual
  // open/closed state only takes over once the query is cleared.
  const isGroupExpanded = title =>
    normalizedQuery.value ? true : (manuallyExpanded[title] ?? false);

  const setGroupExpanded = (title, value) => {
    manuallyExpanded[title] = value;
  };

  // Called directly from the template's v-for rather than through a computed, so it reruns on
  // every render of the enclosing component — including ones triggered by state this composable
  // doesn't own (e.g. an unrelated field changing elsewhere in the form). Memoized per (text,
  // query) so those re-renders reuse the previous split instead of re-scanning every label again.
  const splitSegments = memoize(
    (text, query) => {
      const lowerText = text.toLowerCase();
      const segments = [];
      let start = 0;
      let index = lowerText.indexOf(query, start);

      while (index !== -1) {
        if (index > start) segments.push({ text: text.slice(start, index), matched: false });
        segments.push({ text: text.slice(index, index + query.length), matched: true });
        start = index + query.length;
        index = lowerText.indexOf(query, start);
      }
      if (start < text.length) segments.push({ text: text.slice(start), matched: false });

      return segments;
    },
    (text, query) => `${query}\u0000${text}`,
  );
  const highlight = text => {
    if (!normalizedQuery.value) return [{ text, matched: false }];
    return splitSegments(text, normalizedQuery.value);
  };

  return { query, filteredGroups, isGroupExpanded, setGroupExpanded, highlight };
};
